import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { I3ThemeProvider } from './context/I3ThemeContext';
import { I3ModalProvider } from './context/I3ModalContext';
import I3Nav from './components/I3Nav';
import I3Footer from './components/I3Footer';
import I3ModalContent from './components/I3ModalContent';
import I3IntroLanding from './components/I3IntroLanding';
import { VideoPlaceholder } from '../../components/MediaPlaceholder';
import { lnAssets } from './utils/lnAssets';
import './styles/I3Themes.css';
import './styles/I3Layout.css';
import './styles/I3BridgeButtons.css';
import './styles/I3Cinematic.css';
import './styles/I3EarthTheme.css';
import './styles/LisaBrandOverrides.css';

/** Must be ≥ intro overlay fade duration */
const INTRO_EXIT_MS = 650;

const ABOUT_PATH = '/about';

/** Roses video: full-strength layer + sharp scrim (see I3Layout `--sharp`). Home included so nav/header match inner pages; About stays hidden. */
function isWideRosesHeroPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, '');
  if (p === ABOUT_PATH) return false;
  return (
    p === '/' ||
    p === '/services' ||
    p === '/contact' ||
    p === '/blog' ||
    p.startsWith('/blog/')
  );
}

const I3AppContent: React.FC = () => {
  const location = useLocation();
  const pathNorm = location.pathname.replace(/\/$/, '');
  const isAboutPage = pathNorm === ABOUT_PATH;
  const isWideRosesHero = isWideRosesHeroPath(location.pathname);
  const [introDone, setIntroDone] = useState(false);
  const [introExiting, setIntroExiting] = useState(false);

  const handleIntroComplete = () => {
    setIntroExiting(true);
    window.setTimeout(() => {
      setIntroDone(true);
      setIntroExiting(false);
    }, INTRO_EXIT_MS);
  };

  return (
    <>
      {/*
        Shell always mounts under the intro (z-index 9999). Delaying shell until onComplete forced a
        duplicate intro nav bar during the transition phase — the beige strip users saw. Real I3Nav
        is the only top nav now.
      */}
      <div
        className={`i3-global-video-backdrop${isAboutPage ? ' i3-global-video-backdrop--hidden' : ''}${isWideRosesHero ? ' i3-global-video-backdrop--sharp' : ''}`}
        aria-hidden
      >
        <VideoPlaceholder
          className="i3-global-video-backdrop-video"
          src={lnAssets.videos.landing}
          label="The Bridge — calm sunrise beach background"
          playbackRate={0.78}
        />
        <div className="i3-global-video-backdrop-scrim" aria-hidden />
      </div>
      <div
        className={`i3-app i3-app-over-video${introExiting || introDone ? ' i3-app--intro-revealed' : ' i3-app--intro-pending'}${isAboutPage ? ' i3-app-about-page' : ''}${isWideRosesHero ? ' i3-app-wide-roses-hero' : ''}`}
      >
        <I3Nav />
        <main className="i3-main">
          <Outlet />
        </main>
        <I3Footer />
        <I3ModalContent />
      </div>
      {!introDone && (
        <I3IntroLanding
          onComplete={handleIntroComplete}
          isExiting={introExiting}
          appShellVisible={introExiting}
        />
      )}
    </>
  );
};

const I3Shell: React.FC = () => {
  return (
    <I3ThemeProvider>
      <I3ModalProvider>
        <I3AppContent />
      </I3ModalProvider>
    </I3ThemeProvider>
  );
};

export default I3Shell;
